import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, AlertTriangle, ShieldAlert, RefreshCw, AlertCircle } from 'lucide-react';

const severityStyles = {
  critical: 'border-l-red-500 bg-red-500/5',
  warning: 'border-l-yellow-500 bg-yellow-500/5',
  info: 'border-l-blue-500 bg-blue-500/5',
};

const typeIcons = {
  needs_intervention: AlertTriangle,
  dispute_created: ShieldAlert,
  communication_failure: AlertCircle,
  refund: RefreshCw,
  fraud_block: ShieldAlert,
};

export const NotificationBell = ({ notifications, unreadCount, markAsRead }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const preview = notifications.slice(0, 10);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-muted transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] flex items-center justify-center bg-accent text-accent-foreground text-[10px] font-bold rounded-full px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-card border border-white/10 rounded-xl shadow-2xl z-50" data-testid="notification-dropdown">
          <div className="p-3 border-b border-white/5 flex items-center justify-between">
            <p className="text-sm font-medium">Notifications</p>
            <Link 
              to="/admin/notifications" 
              className="text-xs text-accent hover:underline"
              onClick={() => setOpen(false)}
            >
              View all
            </Link>
          </div>

          {preview.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No notifications
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {preview.map((n) => {
                const Icon = typeIcons[n.type] || AlertCircle;
                return (
                  <button
                    key={n.id}
                    className={`w-full text-left px-3 py-3 border-l-2 hover:bg-muted/30 transition-colors ${severityStyles[n.severity] || ''} ${!n.read_at ? 'opacity-100' : 'opacity-60'}`}
                    onClick={() => { markAsRead(n.id); }}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{n.message}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
